import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { auth } from "./firebase";
import { signOut } from "firebase/auth";

import Register from "./Register";
import Login from "./Login";
import Home from "./Home";
import Marketing from "./Marketing";
import PodProgramme from "./PodProgramme";
import Contact from "./contact";
import Innovation from "./Innovation";
import ProQuote from "./Proquote";
import Dashboard from "./Dashboard";
import ProCRM from "./ProCRM";
import SmartChatbot from "./SmartChatbot";
import CommercialAutomation from "./CommercialAutomation";

import "./App.css";
import "./assest_design/Home.css";
import logo from "./assets/procode_za_logo.jpg";

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  /*
    Hide public website nav on:
    - login page
    - ProCRM page

    ProCRM must feel like a real internal CRM,
    not a public marketing website page.
  */
  const hideNav =
    location.pathname === "/login" ||
    location.pathname.startsWith("/procrm");

  /* ===========================
     ACCESS CONTROL
  =========================== */

  const ALLOWED_INTERNAL_USERS = [
    "leighcheleighche28@gmail.com",
    "leighche@procodeza.co.za",
    "henrique@procodeza.co.za",
  ];

  const canAccessInternal =
    user && ALLOWED_INTERNAL_USERS.includes(user.email);

  /* ===========================
     AUTH
  =========================== */

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  return (
    <div className="app-shell">
      {!hideNav && (
        <header className="home-nav">
          <div className="home-nav-left">
            <img src={logo} alt="ProCode" className="home-logo" />
            <span className="home-brand">ProCode ZA</span>
          </div>

          <div className="home-nav-right">
            <nav className="nav-links">
              <Link to="/home" className="nav-link">
                Home
              </Link>

              <Link to="/marketing" className="nav-link">
                Brand & Growth Support
              </Link>

              <div className="nav-dropdown">
                <button type="button" className="nav-link nav-dropdown-btn">
                  Solutions <span className="nav-dropdown-icon">▾</span>
                </button>

                <div className="nav-dropdown-menu">
                  <Link to="/innovation" className="nav-dropdown-item">
                    <span className="nav-dropdown-title">Client Innovation</span>
                    <span className="nav-dropdown-desc">
                      Custom platforms, AI tools, research systems and business apps.
                    </span>
                  </Link>

                  <Link
                    to="/commercial-automation"
                    className="nav-dropdown-item"
                  >
                    <span className="nav-dropdown-title">
                      Commercial IoT & Automation Solutions
                    </span>
                    <span className="nav-dropdown-desc">
                      Smart tracking, attendance, sensors, offline systems and operations automation.
                    </span>
                  </Link>
                </div>
              </div>

              <Link to="/pod-programme" className="nav-link">
                Pod Programme
              </Link>

              <Link to="/contact" className="nav-link">
                Contact
              </Link>

              {canAccessInternal && (
                <Link to="/dashboard" className="nav-link">
                  Dashboard
                </Link>
              )}

              {canAccessInternal && (
                <Link to="/procrm" className="nav-link">
                  ProCRM
                </Link>
              )}

              <Link to="/proquote" className="nav-link nav-proquote">
                ProQuote
              </Link>
            </nav>

            {user && (
              <div className="user-badge">
                <span className="user-initial">
                  {user.email?.[0].toUpperCase()}
                </span>
                <span className="user-email">{user.email}</span>
              </div>
            )}

            {user ? (
              <button className="logout-btn" onClick={handleLogout}>
                Logout
              </button>
            ) : (
              <button
                className="logout-btn"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
            )}

            <button
              className="hamburger-btn"
              aria-label="Open menu"
              onClick={() =>
                document.body.classList.toggle("mobile-nav-open")
              }
            >
              ☰
            </button>

            {!hideNav && <SmartChatbot />}
          </div>
        </header>
      )}

      {!hideNav && (
        <div className="mobile-nav">
          <Link
            to="/home"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Home
          </Link>

          <Link
            to="/marketing"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Brand & Growth Support
          </Link>

          <div className="mobile-nav-section-title">Solutions</div>

          <Link
            to="/innovation"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Client Innovation
          </Link>

          <Link
            to="/commercial-automation"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Commercial IoT & Automation Solutions
          </Link>

          <Link
            to="/pod-programme"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Pod Programme
          </Link>

          <Link
            to="/proquote"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            ProQuote
          </Link>

          <Link
            to="/contact"
            onClick={() => document.body.classList.remove("mobile-nav-open")}
          >
            Contact
          </Link>

          {canAccessInternal && (
            <Link
              to="/dashboard"
              onClick={() => document.body.classList.remove("mobile-nav-open")}
            >
              Dashboard
            </Link>
          )}

          {canAccessInternal && (
            <Link
              to="/procrm"
              onClick={() => document.body.classList.remove("mobile-nav-open")}
            >
              ProCRM
            </Link>
          )}
        </div>
      )}

      <main className="main-area">
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />

          <Route path="/login" element={<Login />} />

          <Route
            path="/register"
            element={
              <PageWrapper title="Create an account">
                <Register />
              </PageWrapper>
            }
          />

          <Route path="/home" element={<Home />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/pod-programme" element={<PodProgramme />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/innovation" element={<Innovation />} />
          <Route path="/proquote" element={<ProQuote />} />

          <Route
            path="/commercial-automation"
            element={<CommercialAutomation />}
          />

          <Route
            path="/dashboard"
            element={
              canAccessInternal ? (
                <Dashboard />
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />

          <Route
            path="/procrm"
            element={
              canAccessInternal ? (
                <ProCRM />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function PageWrapper({ children, title }) {
  return (
    <div className="page">
      <section className="panel">
        {title && <h1 className="panel-title">{title}</h1>}
        {children}
      </section>
    </div>
  );
}

export default App;